/**
 * Dated, per-user note entries for demand and debtor records.
 * Matter notes persist in localStorage; debtor notes live on the contact profile.
 */

import { listDebtorProfiles, upsertDebtorProfile } from './debtorProfiles.js';

const MATTER_STORAGE_KEY = 'demandjet.matterNotes';

function readMatterMap() {
  try {
    const raw = localStorage.getItem(MATTER_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeMatterMap(map) {
  localStorage.setItem(MATTER_STORAGE_KEY, JSON.stringify(map || {}));
}

function newNoteId() {
  return `note_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Normalize legacy string notes or mixed storage into entry arrays.
 * @returns {{ id: string, body: string, createdAt: string, authorName: string, authorEmail: string }[]}
 */
export function normalizeNoteEntries(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const body = String(item.body || item.text || item.notes || '').trim();
        if (!body) return null;
        return {
          id: String(item.id || newNoteId()),
          body,
          createdAt: String(item.createdAt || item.Created_At || new Date().toISOString()),
          authorName: String(item.authorName || item.Actor_Name || item.author || 'Team member'),
          authorEmail: String(item.authorEmail || item.email || ''),
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  }

  if (typeof raw === 'string' && raw.trim()) {
    return [
      {
        id: newNoteId(),
        body: raw.trim(),
        createdAt: new Date().toISOString(),
        authorName: 'Team member',
        authorEmail: '',
      },
    ];
  }

  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.entries)) return normalizeNoteEntries(raw.entries);
    if (typeof raw.notes === 'string' && raw.notes.trim()) {
      return normalizeNoteEntries(raw.notes);
    }
  }

  return [];
}

export function buildNoteEntry({ body, authorName, authorEmail, createdAt } = {}) {
  const text = String(body || '').trim();
  if (!text) return null;
  return {
    id: newNoteId(),
    body: text,
    createdAt: createdAt || new Date().toISOString(),
    authorName: String(authorName || 'Team member').trim() || 'Team member',
    authorEmail: String(authorEmail || '').trim(),
  };
}

export function formatNoteWhen(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const time = d.toLocaleString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${mm}.${dd}.${yy} ${time}`;
  } catch {
    return String(value);
  }
}

function matterBucket(matterId) {
  const id = String(matterId || '');
  if (!id) return { id: '', entries: [] };
  const stored = readMatterMap()[id];
  return { id, entries: normalizeNoteEntries(stored) };
}

export function listMatterNoteEntries(matterId) {
  return matterBucket(matterId).entries;
}

export function addMatterNoteEntry(matterId, extras = {}) {
  const id = String(matterId || '');
  if (!id) return null;
  const entry = buildNoteEntry(extras);
  if (!entry) return null;
  const map = readMatterMap();
  const existing = normalizeNoteEntries(map[id]);
  const entries = [entry, ...existing];
  map[id] = { entries, updatedAt: entry.createdAt };
  writeMatterMap(map);
  return entry;
}

export function deleteMatterNoteEntry(matterId, noteId) {
  const id = String(matterId || '');
  const target = String(noteId || '');
  if (!id || !target) return false;
  const map = readMatterMap();
  const existing = normalizeNoteEntries(map[id]);
  const entries = existing.filter((item) => String(item.id) !== target);
  if (entries.length === existing.length) return false;
  if (!entries.length) {
    delete map[id];
  } else {
    map[id] = { entries, updatedAt: new Date().toISOString() };
  }
  writeMatterMap(map);
  return true;
}

/** Overlay note entries onto matter rows. */
export function applyMatterNotes(matters = []) {
  const map = readMatterMap();
  return (matters || []).map((matter) => {
    const id = String(matter?.ROWID || '');
    if (!id) return matter;
    const entries = normalizeNoteEntries(map[id] || matter.Client_Note_Entries);
    if (!entries.length && !matter.Client_Note_Entries) return matter;
    return {
      ...matter,
      Client_Note_Entries: entries,
      Client_Notes: entries.map((n) => n.body).join('\n\n') || null,
    };
  });
}

/** @deprecated Legacy single-string API — prefer listMatterNoteEntries. */
export function getMatterNotes(matterId) {
  return listMatterNoteEntries(matterId)
    .map((n) => n.body)
    .join('\n\n');
}

/** @deprecated Legacy single-string API — prefer addMatterNoteEntry. */
export function setMatterNotes(matterId, notes) {
  const id = String(matterId || '');
  if (!id) return '';
  const map = readMatterMap();
  const text = String(notes || '').trim();
  if (!text) {
    delete map[id];
    writeMatterMap(map);
    return '';
  }
  map[id] = {
    entries: normalizeNoteEntries(text),
    updatedAt: new Date().toISOString(),
  };
  writeMatterMap(map);
  return text;
}

export function listDebtorNoteEntries(profileOrDebtor) {
  if (!profileOrDebtor) return [];
  if (Array.isArray(profileOrDebtor.notesEntries)) {
    return normalizeNoteEntries(profileOrDebtor.notesEntries);
  }
  if (Array.isArray(profileOrDebtor.Notes_Entries)) {
    return normalizeNoteEntries(profileOrDebtor.Notes_Entries);
  }
  return normalizeNoteEntries(profileOrDebtor.notes || profileOrDebtor.Notes || '');
}

export function addDebtorNoteEntry(email, debtor, extras = {}) {
  if (!debtor) return null;
  const entry = buildNoteEntry(extras);
  if (!entry) return null;

  const existing = listDebtorNoteEntries(debtor);
  const notesEntries = [entry, ...existing];
  if (!saveDebtorNoteEntries(email, debtor, notesEntries)) return null;
  return entry;
}

function saveDebtorNoteEntries(email, debtor, notesEntries) {
  if (!debtor) return false;
  upsertDebtorProfile(email, {
    id: debtor.id,
    previousId: debtor.id,
    Debtor_Name: debtor.name || debtor.Debtor_Name || '',
    Debtor_Company: debtor.company || debtor.Debtor_Company || '',
    Debtor_State: debtor.state || debtor.Debtor_State || '',
    Debtor_Country: debtor.country || debtor.Debtor_Country || 'US',
    Debtor_Street: debtor.street || debtor.Debtor_Street || '',
    Debtor_City: debtor.city || debtor.Debtor_City || '',
    Debtor_Postal_Code: debtor.postalCode || debtor.Debtor_Postal_Code || '',
    Debtor_Email: debtor.email || debtor.Debtor_Email || '',
    notesEntries,
    notes: notesEntries.map((n) => n.body).join('\n\n'),
  });
  return true;
}

export function deleteDebtorNoteEntry(email, debtor, noteId) {
  if (!debtor) return false;
  const target = String(noteId || '');
  if (!target) return false;

  const fromProfile = findDebtorProfileNotes(email, debtor.id);
  const existing = fromProfile.length ? fromProfile : listDebtorNoteEntries(debtor);
  const notesEntries = existing.filter((item) => String(item.id) !== target);
  if (notesEntries.length === existing.length) return false;

  return saveDebtorNoteEntries(email, debtor, notesEntries);
}

/** Ensure profile list carries notesEntries when building debtor records. */
export function notesEntriesFromProfile(profile) {
  return listDebtorNoteEntries(profile);
}

export function findDebtorProfileNotes(email, debtorId) {
  const list = listDebtorProfiles(email);
  const match = list.find((item) => String(item.id) === String(debtorId));
  return listDebtorNoteEntries(match || {});
}
